import { useCallback, useEffect, useRef, useState } from 'react'
import { createContainer } from 'unstated-next'
import MediaPermissionsStore from '../../PrecallTest/Components/Permissions/Stores/MediaPermissionsStore'
import { highResVideoDimensions } from '../../VideoCallV2/Config/videoInput'
import { getMediaDevices } from '../../VideoCallV2/Helpers/MediaDevices'
import { getVideoTrack } from '../../VideoCallV2/Helpers/VideoInput'
import { RecordRTCPromisesHandler } from 'recordrtc'
import { audioConfig, videoConfig } from '../Config'
import { useList } from 'react-use'
import { notify } from '../../../Helpers/Notifications'

const VideoRecordStore = createContainer(() => {
  const permissions = MediaPermissionsStore.useContainer()

  const stopVid = useRef(() => {})
  const stopMic = useRef(() => {})
  const pauseRecording = useRef(() => {})
  const resumeRecording = useRef(() => {})
  const submit = useRef(() => {})
  const videoPlayer = useRef(null)
  const [cameras, { set: updateCameras }] = useList([])
  const [mics, { set: updateMics }] = useList([])
  const [activeCamera, setActiveCamera] = useState({})
  const [activeMic, setActiveMic] = useState({})
  const [recordingState, setRecordingState] = useState('NOT RECORDED')
  const [recordedVideo, setRecordedVideo] = useState(null)
  const [isPaused, setIsPaused] = useState(false)
  const [sizeLimit, setSizeLimit] = useState(50 * 1024 * 1024)

  const initCamera = useCallback(async () => {
    stopVid.current()

    const [track, stop] = await getVideoTrack(
      activeCamera?.deviceId,
      highResVideoDimensions,
      false,
    )

    if (videoPlayer.current) {
      videoPlayer.current.srcObject = new MediaStream([track])
    }

    stopVid.current = stop
  }, [activeCamera])

  useEffect(() => {
    if (permissions.some((p) => p !== 'granted')) {
      navigator.mediaDevices
        .getUserMedia({
          video: videoConfig,
          audio: true,
        })
        .then(async (stream) => {
          const cameras = await getMediaDevices('videoinput')
          if (cameras.length === 0) {
            notify('error', 'No cameras found')
            return
          }
          if (activeCamera?.deviceId === '') setActiveCamera(cameras[0])
          stopVid.current = () =>
            stream.getTracks().forEach(function (track) {
              track.stop()
            })
        })
        .catch(() => {
          if (cameras.length === 0) {
            console.log('denied')
            notify('error', 'No cameras found')
            return
          }
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions, setActiveCamera])

  useEffect(() => {
    ;(async () => {
      stopVid.current()
      stopMic.current()

      const cameras = await getMediaDevices('videoinput')
      const mics = await getMediaDevices('audioinput')

      updateCameras(cameras)
      setActiveCamera(cameras[0])
      updateMics(mics)
      setActiveMic(mics[0])
    })()
  }, [updateCameras, updateMics])

  useEffect(() => {
    if (
      activeCamera?.deviceId &&
      videoPlayer.current &&
      recordingState === 'NOT RECORDED'
    ) {
      ;(async () => await initCamera())()
    }
  }, [activeCamera, initCamera, recordingState])

  const startRecording = useCallback(async () => {
    if (recordingState === 'NOT RECORDED') {
      if (stopVid.current) stopVid.current()

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: activeCamera?.deviceId,
          ...videoConfig,
        },
        audio: {
          deviceId: activeMic?.deviceId,
          ...audioConfig,
        },
      })

      let recorder = new RecordRTCPromisesHandler(stream, {
        type: 'video',
        mimeType: 'video/mp4',
      })

      recorder.startRecording()
      videoPlayer.current.srcObject = stream
      setRecordingState('RECORDING')
      videoPlayer.current.muted = true
      videoPlayer.current.volume = 0

      recorder.current = stream

      pauseRecording.current = () => {
        setIsPaused(true)
        recorder.pauseRecording()
      }
      resumeRecording.current = () => {
        setIsPaused(false)
        recorder.resumeRecording()
      }

      stopVid.current = async () => {
        await recorder.stopRecording()
        stream.getTracks().forEach((track) => track.stop())
        const blob = await recorder.getBlob()
        await recorder.reset()
        await recorder.destroy()

        pauseRecording.current = () => {}
        resumeRecording.current = () => {}

        return blob
      }
    }
  }, [recordingState, activeCamera, activeMic])

  const deleteRecording = useCallback(async () => {
    stopVid.current()
    setRecordedVideo(null)
    videoPlayer.current.src = ''
    videoPlayer.current.controls = ''
    setRecordingState('NOT RECORDED')
    await initCamera()
  }, [initCamera])

  const stopRecording = useCallback(async () => {
    if (recordingState === 'RECORDING') {
      setRecordingState('RECORDED')
      const blob = await stopVid.current()

      if (blob.size > sizeLimit) {
        notify(
          'error',
          'Video size should be less than 50MB. Please record a shorter video.',
        )
        await deleteRecording()
      } else {
        setRecordedVideo(blob)

        videoPlayer.current.srcObject = null
        videoPlayer.current.src = URL.createObjectURL(blob)
        videoPlayer.current.controls = 'controls'
        videoPlayer.current.muted = false
        videoPlayer.current.volume = 1

        stopVid.current = () => {}
        await submit.current(blob)
      }
    }
  }, [recordingState, deleteRecording, sizeLimit])

  return {
    videoPlayer,
    stopVid,
    recordingState,
    setRecordingState,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    recordedVideo,
    deleteRecording,
    cameras,
    activeCamera,
    mics,
    setActiveCamera,
    activeMic,
    setActiveMic,
    isPaused,
    setIsPaused,
    submit,
    setSizeLimit,
  }
})

export default VideoRecordStore
